import React from "react";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

/**
 * 상세 보기용 공통 모달.
 * 폼(저장/취소 버튼)이 없는 rich-content 뷰어에 사용.
 *
 * props:
 *   open       - boolean
 *   onClose    - () => void
 *   title      - string | ReactNode
 *   subtitle   - string (optional)
 *   maxWidth   - "xs"|"sm"|"md"|"lg"|"xl"  (default: "lg")
 *   height     - string  (default: "88vh")
 *   padding    - number  (default: 2.5)  — 스크롤 영역 내부 패딩
 *   children   - ReactNode
 */
export default function DetailModal({
    open,
    onClose,
    title,
    subtitle,
    maxWidth = "lg",
    height = "88vh",
    padding = 2.5,
    children,
}) {
    // 숫자를 주면 그 픽셀 폭으로 연다. MUI 브레이크포인트(lg=1200)로는 표 형태 본문에
    // 폭이 모자라는 화면이 있어서, 그런 곳만 px 로 직접 지정할 수 있게 열어 둔다.
    const isPx = typeof maxWidth === "number";

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={isPx ? false : maxWidth}
            fullWidth
            PaperProps={{
                sx: {
                    height,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 1.5,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 18px 60px rgba(15,23,42,0.12)",
                    ...(isPx && { maxWidth: `${maxWidth}px`, width: "100%" }),
                },
            }}
        >
            {/* 헤더 */}
            <Box sx={{
                display: "flex", alignItems: "center",
                px: 2.5, py: 1.25,
                borderBottom: "1px solid #e2e8f0",
                flexShrink: 0,
            }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.2 }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ ml: 1 }}>
                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            {/* 스크롤 콘텐츠 영역 */}
            <Box sx={{ flex: 1, overflowY: "auto", p: padding }}>
                {children}
            </Box>
        </Dialog>
    );
}
